import type { FetchError } from 'ofetch'
import type { IconLibProps } from '~/components/tools/icon.vue'
import type { PricingCardPattern } from '~/contents/pricing'

export type ShortBenefitsContext = {
	[id: string]: {
		icon: {
			dir: string
			name: string
		}
		isOpen: boolean
		value:
			| string[]
			| {
					link?: {
						text: string
						url: string
					}
					text: string
			  }[]
	}
}

export type IRequestResponse<T = any> = {
	responseCode: number
	responseText: string
	data: T
	error?: FetchError<any>
}

export type ProductPrice = {
	id: string
	product_variation_id: string
	genius_price: string
	market_price: string
	cover_period: string
	month: string
	discount_percentage: number
	is_dynamic_price: boolean
	preferred?: boolean
}

export enum ProductColors {
	DEFAULT = '#26554C',
	HEALTH = '#EE46BC',
	AUTO = '#FF9532',
	GADGETS = '#4F1748',
	TRAVEL = '#6F56DC'
}

export type ProductContext = Array<{
	id: string
	name: string
	code_name: number
	product_color: string
	product_category_id: string
	mca_product_id: string
	order_in_category: number
	benefits: any
	link_name?: string
	renewable: boolean
	claimable: boolean
	cover_period?: string
	form_name: string
	market_price: string | number
	is_dynamic_price: boolean
	inspectable: boolean
	certificateable: boolean
	active: boolean
	need_to_know?: Array<any>
	others: any
	exclusions?: Array<string>
	how_to_claim?: Array<string>
	created_by: string
	updated_by: string
	group_id?: string
	supporting_document: any
	short_benefits?: Array<string>
	how_to_get_covered?: Array<string>
	alternate_id?: string
	alternative_price?: number | string
	provider_percentage: number
	provider_id: any
	underwriter: string
	hospital_info?: string
	show: boolean
	alternate_name: string
	short_description?: string
	description: any
	created_at: string
	updated_at: string
	deleted_at: any
	price: ProductPrice
	prices: Array<ProductPrice>
	productCategory: {
		id: string
		name: string
		description: string
		active: boolean
	}
	pricingBenefits?: {
		name: {
			text: string
		}
		description: {
			text: string
		}
		price?: {
			text: string | number
			ui?: string
		}
		tag?: {
			text?: string[]
			ui?: string
		}
		benefit_highlight?: Array<{
			text: string
			notAllowed?: boolean
		}>
		dedicated_benfit?: PricingCardPattern['benefit_highlight']
		individual_benefit?: PricingCardPattern['benefit_highlight']
		individualTag?: string
		card_icon?: {
			onhover?: IconLibProps
			default?: IconLibProps
		}
		actions?: {
			text: string
			ui?: string
		}
	}
}>

export type PaginationContext<T = any> = {
	per_page: number
	total_pages: number
	total_count?: number | string
	count?: number | string
	current_page: number
	filter?: string | number
	path: string
	query?: string
	items?: T[]
}
export type ProductCategoryContext = Array<{
	id: string
	name: string
	description: string
	active: boolean
	created_at: string
	updated_at: string
	deleted_at: any
}>
export type LocalLgaStateContext = { state?: string; lgas?: string[]; value: string; label: string }

export type NotificationContext = {
	notificationTitle?: string
	notificationMessage?: string
	notificationStatus: number | null | string
}
export type ClassContext =
	| string
	| Array<string | { [key: string]: unknown } | undefined | ClassContext>
	| { [key: string]: unknown }

export type HospitalListResponse = {
	id: string
	external_id: number
	name: string
	location: string
	address: string
	type: number
	active: boolean
}

export enum ProductCategory {
	Health = 'health',
	Auto = 'auto',
	Gadget = 'gadget',
	Travel = 'travel',
	Others = 'others'
}

export enum SupportComms {
	Line = '+2349070008899',
	Whatsapp = 'https://wa.link/qo44ms'
}
export enum ChannelSource {
	TWITTER = 'twitter',
	INSTAGRAM = 'instagram',
	GOOGLE = 'google',
	FACEBOOK = 'facebook',
	WEBSITE = 'website',
	BDM = 'bdm',
	SALES_TEAM = 'sales_team',
	SALESAGENT = 'sales_agent'
}
export enum ProductCode {
	COMPREHENSIVE_AUTO = 2,
	MINI_COMPREHENSIVE = 4,
	THIRD_PARTY = 1,
	MONTHLY_COMPREHENSIVE = 14,
	ACCESS_PLAN = 21,

	GADGET = 5,
	LAPTOP_COVER = 30,
	TRAVEL_ALLIANZ = 29,
	TRAVEL_AIICO = 6,

	FLEXICARE_HYGEIA_BUSINESS = 31,
	FLEXICARE_BASTION_BUSINESS = 38,
	FLEXICARE_BASTION_PERSONAL = 9,
	FLEXICARE_PLUS_HYGEIA = 26,

	FLEXICARE_MINI_HYGEIA_BUSINESS = 32,
	FLEXICARE_MINI_BASTION_BUSINESS = 39,
	FLEXICARE_MINI_BASTION_PERSONAL = 20,

	ZENCARE_BASTION_PERSONAL = 17,
	ZENCARE_BASTION_BUSINESS = 33,
	ZENCARE_ELALAN_BASTION_BUSINESS = 43,
	ZENCARE_PLUS_BASTION_PERSONAL = 16,
	ZENCARE_PLUS_BASTION_BUSINESS = 34,

	ZENCARE_PRIME_BASTION_PERSONAL = 18,
	ZENCARE_PRIME_BASTION_BUSINESS = 35,

	ZENCARE_SENIOR_CITIZEN_NON_WAITING_HYGEIA = 22,
	ZENCARE_SENIOR_CITIZEN_WAITING_HYGEIA = 23,
	ZENCARE_PLUS_SENIOR_CITIZEN_NON_WAITING_HYGEIA = 24,
	ZENCARE_PLUS_SENIOR_CITIZEN_WAITING_HYGEIA = 25,

	PRIMECARE_BASTION_PERSONAL = 27,
	PRIMECARE_BASTION_BUSINESS = 36,
	PRIMECARE_PLUS_BASTION_PERSONAL = 28,
	PRIMECARE_PLUS_BASTION_BUSINESS = 37,

	SENIORS_PLAN_BASTION = 40,
	SENIORS_PLAN_PLUS_BASTION = 41,
	SENIORS_PLAN_PRIME_BASTION = 42
}
export type ChannelsStorage = { time_stamp: string; utm_source: string; date_created?: string }
